import { useState, useEffect } from 'react'
import {
  createSnippet,
  updateSnippet,
  deleteSnippet,
  subscribeToSnippetsByDate,
  subscribeToUserSnippets,
} from '@/lib/firestore'
import type { Snippet, SnippetInput, SnippetUpdate } from '@/types'

interface UseSnippetsOptions {
  userId: string
  date?: string // If provided, only fetch snippets for this date
}

interface UseSnippetsReturn {
  snippets: Snippet[]
  loading: boolean
  error: Error | null
  create: (input: SnippetInput) => Promise<void>
  update: (id: string, update: SnippetUpdate) => Promise<void>
  remove: (id: string) => Promise<void>
}

export const useSnippets = ({
  userId,
  date,
}: UseSnippetsOptions): UseSnippetsReturn => {
  const [snippets, setSnippets] = useState<Snippet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = date
      ? subscribeToSnippetsByDate(
          userId,
          date,
          (snippets) => {
            setSnippets(snippets)
            setLoading(false)
          },
          (err) => {
            setError(err)
            setLoading(false)
          }
        )
      : subscribeToUserSnippets(
          userId,
          (snippets) => {
            setSnippets(snippets)
            setLoading(false)
          },
          (err) => {
            setError(err)
            setLoading(false)
          }
        )

    return unsubscribe
  }, [userId, date, refreshToken])

  const create = async (input: SnippetInput) => {
    try {
      await createSnippet(userId, input)
    } catch (err) {
      setError(err as Error)
      throw err
    }
  }

  const update = async (id: string, updateData: SnippetUpdate) => {
    const previousSnippets = snippets
    try {
      // Optimistic update
      setSnippets((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, content: updateData.content, updatedAt: new Date() }
            : s
        )
      )
      await updateSnippet(id, updateData)
    } catch (err) {
      setError(err as Error)
      setSnippets(previousSnippets)
      setRefreshToken(Date.now())
      throw err
    }
  }

  const remove = async (id: string) => {
    const previousSnippets = snippets
    try {
      // Optimistic removal
      setSnippets((prev) => prev.filter((s) => s.id !== id))
      await deleteSnippet(id)
    } catch (err) {
      setError(err as Error)
      setSnippets(previousSnippets)
      setRefreshToken(Date.now())
      throw err
    }
  }

  return {
    snippets,
    loading,
    error,
    create,
    update,
    remove,
  }
}
